import React, { useState } from 'react';
import { Modal, Checkbox, Button, message, Typography, Flex } from 'antd';
import { Scene } from '../../../../types/scene';
import { 
  Copy,
  Layers,
  Image,
  FileText,
  Clock,
  Tag,
  ArrowRightLeft,
  X,
  CheckSquare
} from 'lucide-react';
import './CopySettingsModal.scss';

const { Text } = Typography;

interface CopySettingsModalProps {
  visible: boolean;
  sourceScene: Scene | null;
  targetScenes: Scene[];
  onConfirm: (targetSceneIds: string[], settings: string[]) => Promise<void>;
  onCancel: () => void;
}

const SETTING_OPTIONS = [
  { key: 'layout', label: 'Layout Preset', description: 'Camera/screen arrangement', icon: Layers },
  { key: 'background', label: 'Background', description: 'Color, gradient, or image', icon: Image },
  { key: 'script', label: 'Script', description: 'Teleprompter content', icon: FileText },
  { key: 'duration', label: 'Duration', description: 'Target scene length', icon: Clock },
  { key: 'tags', label: 'Tags', description: 'Scene categories', icon: Tag },
  { key: 'transition', label: 'Transition', description: 'Type and duration', icon: ArrowRightLeft },
];

export const CopySettingsModal: React.FC<CopySettingsModalProps> = ({
  visible,
  sourceScene,
  targetScenes,
  onConfirm,
  onCancel,
}) => {
  const [selectedScenes, setSelectedScenes] = useState<string[]>([]);
  const [selectedSettings, setSelectedSettings] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (selectedScenes.length === 0) {
      message.warning('Please select at least one scene');
      return;
    }
    if (selectedSettings.length === 0) {
      message.warning('Please select at least one setting to copy');
      return;
    }

    try {
      setIsLoading(true);
      await onConfirm(selectedScenes, selectedSettings);
      setSelectedScenes([]);
      setSelectedSettings([]);
    } catch (error) {
      console.error('Failed to copy settings:', error);
      message.error('Failed to copy settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedScenes([]);
    setSelectedSettings([]);
    onCancel();
  };

  const handleSelectAllScenes = () => {
    if (selectedScenes.length === targetScenes.length) {
      setSelectedScenes([]);
    } else {
      setSelectedScenes(targetScenes.map(s => s.id));
    }
  };

  const handleSelectAllSettings = () => {
    if (selectedSettings.length === SETTING_OPTIONS.length) {
      setSelectedSettings([]);
    } else {
      setSelectedSettings(SETTING_OPTIONS.map(s => s.key));
    }
  };

  const toggleSetting = (key: string) => {
    if (selectedSettings.includes(key)) {
      setSelectedSettings(selectedSettings.filter(s => s !== key));
    } else {
      setSelectedSettings([...selectedSettings, key]);
    }
  };

  const toggleScene = (sceneId: string) => {
    if (selectedScenes.includes(sceneId)) {
      setSelectedScenes(selectedScenes.filter(s => s !== sceneId));
    } else {
      setSelectedScenes([...selectedScenes, sceneId]);
    }
  };

  const allSettingsSelected = selectedSettings.length === SETTING_OPTIONS.length;
  const allScenesSelected = selectedScenes.length === targetScenes.length && targetScenes.length > 0;

  return (
    <Modal
      open={visible}
      onCancel={isLoading ? undefined : handleCancel}
      closable={false}
      maskClosable={!isLoading}
      footer={null}
      width={600}
      className="copy-settings-modal"
      centered
    >
      <div className="modal-header">
        <div className="header-content">
          <div className="header-icon">
            <Copy size={18} />
          </div>
          <div className="header-text">
            <h3 className="modal-title">Copy Settings</h3>
            <span className="source-scene">from "{sourceScene?.title || 'Scene'}"</span>
          </div>
        </div>
        <button 
          className="close-button" 
          onClick={handleCancel}
          disabled={isLoading}
        >
          <X size={18} />
        </button>
      </div>

      <div className="modal-body">
        <section className="settings-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <CheckSquare size={16} />
            </div>
            <span className="card-title">What to Copy</span>
            <button 
              className="select-all-btn"
              onClick={handleSelectAllSettings}
            >
              {allSettingsSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="settings-grid">
            {SETTING_OPTIONS.map(option => {
              const IconComponent = option.icon;
              const isSelected = selectedSettings.includes(option.key);
              return (
                <div
                  key={option.key}
                  className={`setting-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleSetting(option.key)}
                >
                  <Checkbox checked={isSelected} />
                  <div className="option-icon">
                    <IconComponent size={16} />
                  </div>
                  <div className="option-content">
                    <Text className="option-label">{option.label}</Text>
                    <Text type="secondary" className="option-description">{option.description}</Text>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="settings-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <Layers size={16} />
            </div>
            <span className="card-title">Apply to Scenes</span>
            <button 
              className="select-all-btn"
              onClick={handleSelectAllScenes}
            >
              {allScenesSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="scenes-list">
            {targetScenes.map((scene, index) => {
              const isSelected = selectedScenes.includes(scene.id);
              return (
                <div
                  key={scene.id}
                  className={`scene-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleScene(scene.id)}
                >
                  <Checkbox checked={isSelected} />
                  <span className="scene-index">{index + 1}</span>
                  <span className="scene-title">{scene.title}</span>
                </div>
              );
            })}
            {targetScenes.length === 0 && (
              <div className="empty-state">
                <Text type="secondary">No other scenes available</Text>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="modal-footer">
        <Button 
          className="cancel-button"
          onClick={handleCancel} 
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          className="confirm-button"
          onClick={handleConfirm}
          disabled={selectedScenes.length === 0 || selectedSettings.length === 0}
          loading={isLoading}
        >
          {isLoading 
            ? 'Copying...' 
            : `Copy to ${selectedScenes.length} Scene${selectedScenes.length !== 1 ? 's' : ''}`
          }
        </Button>
      </div>
    </Modal>
  );
};
